import Button from "@mui/material/Button";
import { useFlowRuntime } from "../FlowRuntimeContext";
import React, { useEffect, useMemo, useState } from "react";

import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import ButtonBase from "@mui/material/ButtonBase";
import Typography from "@mui/material/Typography";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import MovieRoundedIcon from "@mui/icons-material/MovieRounded";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import StickyNote2RoundedIcon from "@mui/icons-material/StickyNote2Rounded";

import type {
  FlowComponentRenderProps,
  ProofOfServiceMediaComponentConfig,
  ProofOfServiceMediaItem,
} from "../types";
import HtmlText from "./HtmlText";

const iconByType: Record<ProofOfServiceMediaItem["type"], React.ElementType> = {
  image: ImageRoundedIcon,
  video: MovieRoundedIcon,
  voice: GraphicEqRoundedIcon,
  note: StickyNote2RoundedIcon,
};

function mediaTypeLabel(type: ProofOfServiceMediaItem["type"]) {
  switch (type) {
    case "image":
      return "Photo";
    case "video":
      return "Video";
    case "voice":
      return "Voicemail";
    default:
      return "Note";
  }
}

const ProofOfServiceMediaBlock: React.FC<
  FlowComponentRenderProps<ProofOfServiceMediaComponentConfig>
> = ({ config }) => {
  const { refreshMedia, refreshingMedia } = useFlowRuntime();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [failedMediaUrl, setFailedMediaUrl] = useState("");
  const [failedThumbnailUrl, setFailedThumbnailUrl] = useState("");
  const selectedItem = config.items[selectedIndex] ?? config.items[0];
  const showTabs = config.showTabs !== false;

  const groupedItems = useMemo(
    () => config.items.map((item, index) => ({ ...item, index })),
    [config.items],
  );

  // Equivalent compiler projections must not reset the tab the respondent selected.
  const mediaContents = JSON.stringify(config.items);
  useEffect(() => {
    setSelectedIndex(0);
    setFailedMediaUrl("");
    setFailedThumbnailUrl("");
  }, [mediaContents, config.showTabs]);

  if (!selectedItem) return null;

  const ActiveIcon = iconByType[selectedItem.type];
  const previewImageUrl =
    selectedItem.type === "image"
      ? selectedItem.thumbnailUrl ?? selectedItem.url
      : selectedItem.thumbnailUrl;
  const playableMediaUrl =
    selectedItem.type === "video" || selectedItem.type === "voice"
      ? selectedItem.url
      : undefined;
  const mediaFailed =
    Boolean(playableMediaUrl) && failedMediaUrl === playableMediaUrl;

  return (
    <Card
      sx={{
        bgcolor: "transparent",
        border: 0,
        borderRadius: 0,
        boxShadow: "none",
        color: "var(--cx-brand-text)",
        p: 0,
      }}
    >
      <Stack spacing={1.5}>
        {showTabs ? (
          <Box
            sx={{
              borderBottom: "1px solid var(--cx-brand-border)",
              display: "flex",
              height: 44,
              overflow: "hidden",
              width: "100%",
              '[data-cx-color-scheme="dark"] &': {
                borderBottom: 0,
                gap: 1,
                height: 58,
                order: 2,
              },
            }}
          >
            {groupedItems.map((item) => {
              const selected = selectedIndex === item.index;
              const ItemIcon = iconByType[item.type];

              return (
                <ButtonBase
                  key={`${item.type}-${item.index}`}
                  onClick={() => setSelectedIndex(item.index)}
                  sx={{
                    alignItems: "center",
                    borderBottom: "2px solid",
                    borderColor: selected
                      ? "var(--cx-brand-accent)"
                      : "transparent",
                    color: selected
                      ? "var(--cx-brand-accent)"
                      : "var(--cx-brand-text-muted)",
                    display: "flex",
                    flex: 1,
                    fontSize: { xs: 12, sm: 14 },
                    fontWeight: selected ? 600 : 500,
                    gap: { xs: 0.4, sm: 0.75 },
                    height: "100%",
                    justifyContent: "center",
                    minWidth: 0,
                    opacity: 1,
                    px: { xs: 0.35, sm: 0.75 },
                    touchAction: "manipulation",
                    whiteSpace: "nowrap",
                    '[data-cx-color-scheme="dark"] &': {
                      background: selected
                        ? 'color-mix(in srgb, var(--cx-brand-primary) 15%, transparent)'
                        : 'color-mix(in srgb, var(--cx-brand-button-bg) 72%, transparent)',
                      border: '1px solid',
                      borderColor: selected ? 'var(--cx-brand-primary)' : 'var(--cx-brand-border)',
                      borderRadius: 2.5,
                      height: 56,
                    },
                    "&:hover": {
                      bgcolor: "var(--cx-brand-button-hover-bg)",
                      color: selected
                        ? "var(--cx-brand-accent)"
                        : "var(--cx-brand-text)",
                      opacity: 1,
                    },
                  }}
                  type="button"
                >
                  <ItemIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                  <Box
                    component="span"
                    sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
                  >
                    {mediaTypeLabel(item.type)}
                  </Box>
                </ButtonBase>
              );
            })}
          </Box>
        ) : null}

        <Box
          sx={{
            alignItems: "center",
            bgcolor: "var(--cx-brand-button-bg)",
            border: "1px solid var(--cx-brand-border)",
            borderRadius: 1.5,
            display: "flex",
            justifyContent: "center",
            height: 200,
            overflow: "hidden",
            position: "relative",
            width: "100%",
            '[data-cx-color-scheme="dark"] &': {
              borderRadius: 3.5,
              height: 220,
              order: 1,
              boxShadow: '0 18px 48px rgba(0, 0, 0, 0.24)',
            },
          }}
        >
          {selectedItem.type === "video" &&
          playableMediaUrl &&
          !mediaFailed ? (
            <Box
              aria-label={selectedItem.label}
              component="video"
              controls
              controlsList="nodownload"
              onError={() => setFailedMediaUrl(playableMediaUrl)}
              playsInline
              poster={selectedItem.thumbnailUrl}
              preload="metadata"
              src={playableMediaUrl}
              sx={{
                display: "block",
                height: "100%",
                maxWidth: "100%",
                objectFit: "contain",
                width: "100%",
              }}
            />
          ) : selectedItem.type === "voice" &&
            playableMediaUrl &&
            !mediaFailed ? (
            <Stack
              spacing={1.25}
              sx={{
                alignItems: "center",
                color: "var(--cx-brand-text-muted)",
                px: 2,
                width: "100%",
              }}
            >
              <ActiveIcon sx={{ fontSize: 42 }} />
              <Typography
                variant="body2"
                sx={{ color: "inherit", fontWeight: 700 }}
              >
                {selectedItem.label}
              </Typography>
              <Box
                aria-label={selectedItem.label}
                component="audio"
                controls
                controlsList="nodownload"
                onError={() => setFailedMediaUrl(playableMediaUrl)}
                preload="metadata"
                src={playableMediaUrl}
                sx={{ maxWidth: "100%", width: 320 }}
              />
            </Stack>
          ) : previewImageUrl && failedThumbnailUrl !== previewImageUrl ? (
            <Box
              alt={selectedItem.label}
              component="img"
              onError={() => setFailedThumbnailUrl(previewImageUrl)}
              src={previewImageUrl}
              sx={{ height: "100%", objectFit: "cover", width: "100%" }}
            />
          ) : (
            <Stack
              spacing={1}
              sx={{ alignItems: "center", color: "var(--cx-brand-text-muted)" }}
            >
              <ActiveIcon sx={{ fontSize: 42 }} />
              <Typography
                variant="body2"
                sx={{ color: "inherit", fontWeight: 700 }}
              >
                {selectedItem.label}
              </Typography>
            </Stack>
          )}
        </Box>

        {refreshMedia && (mediaFailed || (Boolean(previewImageUrl) && failedThumbnailUrl === previewImageUrl)) ? (
          <Stack role="status" spacing={1}>
            <Typography variant="body2">This media could not be loaded.</Typography>
            <Button disabled={refreshingMedia} onClick={() => void refreshMedia()}>{refreshingMedia ? "Refreshing media…" : "Refresh media"}</Button>
          </Stack>
        ) : null}
        {selectedItem.type !== "image" && selectedItem.notes?.trim() ? (
          <HtmlText
            component="div"
            html={selectedItem.notes}
            variant="body2"
            sx={{
              color: "var(--cx-brand-text)",
              fontSize: 14,
              lineHeight: "22px",
              '[data-cx-color-scheme="dark"] &': { order: 3 },
            }}
          />
        ) : null}
      </Stack>
    </Card>
  );
};

export default ProofOfServiceMediaBlock;
